import classNames from "classnames";
import { pazienHistory } from "common/components/pazienHistory";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { times } from "lodash-es";

import {
    GET_NOTIFICATIONS,
    MARK_NOTIFICATION_READ,
    NOTIFICATION_SET_ACTIVE
} from "common/actions/snapshots";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";

import { Placeholder } from "common/components/placeholder";
import { Notification } from "./notification";

import "./notifications.scss";

export function Notifications({ fromTopBar, onlyUnread }) {
    const { loading, active_notification } = useSelector((state) => state.snapshots.notifications, shallowEqual);
    const data = useSelector((state) => {
        const storedData = state.snapshots.notifications.data;
        return onlyUnread ? storedData.filter((n) => n.read === false) : storedData;
    });
    const activeSnapshotRef = useSelector((state) => state.snapshots.active);
    const dispatch = useDispatch();

    function pollForNewNotifications({ delay = 0, cancel = false } = {}) {
        const startDate = new Date().toISOString();
        
        return Promise.delay(delay)
            .then(() => {
                dispatch(GET_NOTIFICATIONS(startDate));
            })
            .then(() => pollForNewNotifications({ delay }));
    }

    useEffect(() => {
        dispatch(GET_NOTIFICATIONS());
        const poller = pollForNewNotifications({ delay: 30_000 });

        return () => poller.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!active_notification && activeSnapshotRef && data.length) {
            const selected = data.find(({ snapshot_ref }) => snapshot_ref === activeSnapshotRef);
            NOTIFICATION_SET_ACTIVE(selected._id);
        }
    }, [activeSnapshotRef, active_notification, data, loading]);
    
    function getBucketData(allData) {
        const monthNum = moment.utc().month();
        const prevMonthNum = moment.utc().subtract(1, "months").month();
        const yearNum = moment.utc().year();
        const prevYearNum = moment.utc().subtract(1, "months").year();
        const buckets = [[], [], []];
        
        if (allData.length) {
            buckets[0] = allData.filter((n) => moment.utc(n.date).month() === monthNum && moment.utc(n.date).year() === yearNum);
            buckets[1] = allData.filter((n) => moment.utc(n.date).month() === prevMonthNum && moment.utc(n.date).year() === prevYearNum);
            buckets[2] = allData.filter((n) => !(moment.utc(n.date).month() === monthNum && moment.utc(n.date).year() === yearNum)
                    && !(moment.utc(n.date).month() === prevMonthNum && moment.utc(n.date).year() === prevYearNum));
        }

        return buckets;
    }

    const handleClick = useCallback(({ _id, snapshot_ref, read }) => () => {
        if (fromTopBar) {
            pazienHistory.navigateWithParams(`alerts2/inbox/${snapshot_ref}`, { trigger: true });
        } else {
            if (!read && !onlyUnread) {
                setTimeout(() => { dispatch(MARK_NOTIFICATION_READ(_id, false)); }, 1000);
            }
            NOTIFICATION_SET_ACTIVE(_id, snapshot_ref);
        }
    }, [dispatch, fromTopBar, onlyUnread]);

    const checkKeyUpDown = useCallback((event) => {
        // Noop if data is empty
        if (!data.length) return;

        const currentIndex = data.findIndex(({ _id }) => _id === active_notification);
        if (event.keyCode === 38 && currentIndex > 0) { // Up Arrow, not top of the list
            handleClick(data[currentIndex - 1])();
        }
        if (event.keyCode === 40 && currentIndex < data.length - 1) { // Down Arrow, not end of the list
            handleClick(data[currentIndex + 1])();
        }
    }, [active_notification, data, handleClick]);

    useEffect(() => {
        document.addEventListener("keydown", checkKeyUpDown);
        return () => document.removeEventListener("keydown", checkKeyUpDown);
    }, [checkKeyUpDown]);
    
    const deleteNotification = (notification) => () => {
        dispatch(MARK_NOTIFICATION_READ(notification._id, false));
    };
    
    if (loading) {
        return times(4, (index) => (
            <div className="notification" key={index}>
                <Placeholder size="medium"/>
                <Placeholder size="long"/>
                <Placeholder size="long"/>
            </div>
        ));
    }
    const [thisMonth, lastMonth, older] = getBucketData(data);
    
    return (
        <div className="snapshot-notifications-list">
            <Bucket
                name="THIS MONTH"
                data={thisMonth}
                open
                handleClick={handleClick}
                deleteNotification={deleteNotification}
                activeNotification={active_notification}
                fromTopBar={fromTopBar}
            />
            <Bucket
                name="LAST MONTH"
                data={lastMonth}
                handleClick={handleClick}
                deleteNotification={deleteNotification}
                activeNotification={active_notification}
                fromTopBar={fromTopBar}
            />
            <Bucket
                name="OLDER"
                data={older}
                handleClick={handleClick}
                deleteNotification={deleteNotification}
                activeNotification={active_notification}
                fromTopBar={fromTopBar}
            />
        </div>
    );
}

function Bucket({
    open = false, data, handleClick, deleteNotification, activeNotification, fromTopBar, name
}) {
    const [collapsed, setCollapsed] = useState(!open);
    
    useEffect(() => {
        if (data.some(({ _id }) => _id === activeNotification)) {
            setCollapsed(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeNotification]);

    return (
        <div className={classNames("bucket", { off: collapsed })}>
            <header onClick={() => setCollapsed(!collapsed)}>
                <span><FontAwesomeIcon icon={faAngleRight}/></span> {name} ({data.length})
            </header>
            <div className="bucketPanel">
                {data.map((n) => (
                    <Notification
                        key={n._id}
                        onClick={handleClick(n, 2)}
                        title={n.title}
                        read={n.read}
                        description={n.definition}
                        date={n.date}
                        active={n._id === activeNotification && !fromTopBar}
                        fromTopBar={fromTopBar}
                        clearNotification={deleteNotification(n)}
                    />
                ))}
            </div>
        </div>
    );
}
